/**
 * PulseAssetsCard — fleet-health traffic-light viz for the Pulse Home Dashboard.
 *
 * Renders a `DashboardCardShell` containing:
 *  - A proportional horizontal traffic-light bar (status segments)
 *  - Stat pills: one per status (same StatPill used by PulseActivityCard)
 *  - A status list: each status row with a coloured dot, label, and count
 *
 * Design matches the gripp-os PulseHomeMapView Assets card:
 *  - Horizontal proportional health bar (flex segments, coloured by status)
 *  - Stat pills beneath it (dot + count + label)
 *  - Status list (dot + label + count per row) — the card's asset list
 *
 * NOTE: `useHomeCards().assets` provides `{ total, items: { status, count }[] }`
 * (status slices) not individual named assets. The reference `topAssets` list
 * comes from a different data source not yet in this API. We render the status
 * list here; a named top-3 list can replace this slot when the backend provides
 * per-asset rows (Phase 4 / homeAssetTracks).
 *
 * Map-plottable: pressing the header toggles the map to plot individual assets.
 * Consumes `useHomeCards(range)` — React Query dedupes the request across cards.
 */
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Colors, tokens, Skeleton, EmptyState, ErrorState } from '@gripp/shared-ui';
import { DashboardCardShell, ViewAllLink } from './dashboardCardShell';
import { useHomeCards, type HomeAssetStatusSlice } from '../hooks/useHomeCards';
import { HomeCardProps } from '../types';

// Status → Crop Circle semantic colour. Matches the map markers and the OEM
// donut; here used in the traffic-light bar + stat pills. The card surfaces only
// the attention-needed statuses (yellow + red); "Ready" (green) is intentionally
// omitted from the card — those assets still plot on the map. Green's colour is
// kept here for the shared palette / fallback safety.
const STATUS_COLORS: Record<string, string> = {
  Ready: tokens.colors.semantic.success,          // green — Healthy
  'Needs Repair': tokens.colors.semantic.warning, // yellow — Attention
  'Out of Service': tokens.colors.semantic.error, // red — Action needed
};
const STATUS_FALLBACK = tokens.colors.slate[400]; // chart series fallback: data palette, not a surface — stays constant
const statusColor = (status: string): string =>
  STATUS_COLORS[status] ?? STATUS_FALLBACK;

// Card display label per status — yellow/red reuse the Activity card's
// vocabulary (Pending / Urgent) so the two cards read consistently. Keyed to the
// same i18n strings `pulseActivityCard` uses (home.activity.*). Mapping is by
// colour: warning/yellow → Pending, error/red → Urgent.
const STATUS_LABEL_I18N: Record<string, { key: string; fallback: string }> = {
  'Needs Repair': { key: 'home.activity.pending', fallback: 'Pending' }, // yellow
  'Out of Service': { key: 'home.activity.urgent', fallback: 'Urgent' }, // red
};

// Statuses shown on the card, in display order — the attention-needed ones only
// (yellow then red, matching the Activity card's Pending→Urgent order). Green
// ("Ready") is deliberately excluded; healthy assets still plot on the map.
const CARD_STATUS_ORDER = ['Needs Repair', 'Out of Service'] as const;

// The card's slices: only the attention statuses, in order, filling 0 for any
// the backend omitted. Unlike the OEM card this drops "Ready"/green and any
// unexpected extra statuses — this card is attention-only.
function cardSlices(items: HomeAssetStatusSlice[]): HomeAssetStatusSlice[] {
  const byStatus = new Map(items.map((s) => [s.status, s]));
  return CARD_STATUS_ORDER.map(
    (status) => byStatus.get(status) ?? { status, count: 0 }
  );
}

// ─── TrafficLightBar ──────────────────────────────────────────────────────────
// Proportional horizontal bar: flex segments, coloured by status. Matches the
// reference `<div className="flex h-2.5 rounded-sm overflow-hidden gap-[2px]">`.
interface TrafficLightBarProps {
  items: HomeAssetStatusSlice[];
  total: number;
  testID?: string;
}

const TrafficLightBar: React.FC<TrafficLightBarProps> = ({
  items,
  total,
  testID,
}) => {
  // Each segment gets flex = count / total (proportional width). A 0-count
  // segment is rendered at flex-minimum (0) so it doesn't show but the gap
  // pattern stays stable. Use `overflow: hidden` on the container so the
  // rounded ends clip correctly.
  return (
    <View style={styles.bar} testID={testID}>
      {items.map((s, i) => {
        const flex = total > 0 ? Math.max(s.count / total, 0) : 0;
        if (flex === 0) return null;
        return (
          <View
            key={s.status}
            style={[
              styles.barSegment,
              { flex, backgroundColor: statusColor(s.status) },
              i === 0 && styles.barSegmentFirst,
              i === items.length - 1 && styles.barSegmentLast,
            ]}
          />
        );
      })}
    </View>
  );
};

// ─── StatPill ─────────────────────────────────────────────────────────────────
// Coloured dot + dark count + soft label. Same design as PulseActivityCard's
// StatPill; inline here to keep the card self-contained.
interface StatPillProps {
  color: string;
  count: number;
  label: string;
  active?: boolean;
  dimmed?: boolean;
  onPress?: () => void;
  testID?: string;
}

const StatPill: React.FC<StatPillProps> = ({
  color,
  count,
  label,
  active,
  dimmed,
  onPress,
  testID,
}) => (
  <Pressable
    style={[
      styles.statPill,
      active && styles.statPillActive,
      dimmed && styles.statPillDimmed,
    ]}
    onPress={onPress}
    accessibilityRole="button"
    accessibilityLabel={label}
    testID={testID}
  >
    <View style={[styles.statDot, { backgroundColor: color }]} />
    <Text style={styles.statCount}>{count}</Text>
    <Text style={styles.statLabel}>{label}</Text>
  </Pressable>
);

// ─── StatusRow ────────────────────────────────────────────────────────────────
// Status list row: coloured dot + status label (flex-1) + count. Mirrors the
// reference AssetRow layout, adapted for status slices (no per-asset data yet).
interface StatusRowProps {
  slice: HomeAssetStatusSlice;
  /** Display label (e.g. "Pending"/"Urgent"); the raw status drives colour + testID. */
  label: string;
  active?: boolean;
  dimmed?: boolean;
  onPress?: () => void;
}

const StatusRow: React.FC<StatusRowProps> = ({
  slice,
  label,
  active,
  dimmed,
  onPress,
}) => (
  <Pressable
    style={[
      styles.statusRow,
      active && styles.statusRowActive,
      dimmed && styles.statusRowDimmed,
    ]}
    onPress={onPress}
    accessibilityRole="button"
    accessibilityLabel={label}
    testID={`pulse-assets-status-row-${slice.status.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <View
      style={[styles.statusDot, { backgroundColor: statusColor(slice.status) }]}
    />
    <Text style={styles.statusLabel} numberOfLines={1}>
      {label}
    </Text>
    <Text style={styles.statusCount}>{slice.count}</Text>
  </Pressable>
);

// ─── PulseAssetsCard ──────────────────────────────────────────────────────────

export const PulseAssetsCard: React.FC<HomeCardProps> = ({
  range,
  selected,
  onToggleSelect,
  activeStatus,
  onSelectStatus,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, error } = useHomeCards(range);
  const assets = data?.assets;

  // Display label for a status — yellow/red use the Activity card's vocabulary.
  const labelFor = (status: string): string => {
    const m = STATUS_LABEL_I18N[status];
    return m ? t(m.key, m.fallback) : status;
  };

  const title = t('home.assets.title', 'Assets');
  const action = (
    <ViewAllLink
      label={t('home.cards.viewAll', 'View all')}
      onPress={() => navigate('/assets')}
      testID="pulse-assets-card-view-all"
    />
  );

  if (isLoading) {
    return (
      <DashboardCardShell
        title={title}
        action={action}
        testID="pulse-assets-card"
        loadingSlot={
          <View style={styles.loadingBody}>
            <Skeleton height={10} radius={4} style={styles.loadingBar} />
            <View style={styles.pillRow}>
              <Skeleton width={64} height={24} radius={tokens.borderRadius.full} />
              <Skeleton width={72} height={24} radius={tokens.borderRadius.full} />
              <Skeleton width={80} height={24} radius={tokens.borderRadius.full} />
            </View>
            <View style={styles.loadingList}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} height={18} radius={4} />
              ))}
            </View>
          </View>
        }
      >
        {null}
      </DashboardCardShell>
    );
  }

  if (error) {
    return (
      <DashboardCardShell
        title={title}
        action={action}
        testID="pulse-assets-card"
        errorSlot={
          <ErrorState
            title={t('home.cards.error', 'Could not load data')}
            testID="pulse-assets-card-error"
          />
        }
      >
        {null}
      </DashboardCardShell>
    );
  }

  if (!assets || assets.total === 0) {
    return (
      <DashboardCardShell
        title={title}
        action={action}
        testID="pulse-assets-card"
        emptySlot={
          <EmptyState
            title={t('home.assets.empty', 'No assets for this period')}
            testID="pulse-assets-card-empty"
          />
        }
      >
        {null}
      </DashboardCardShell>
    );
  }

  const slices = cardSlices(assets.items);
  // Non-zero slices only — the bar and pills both skip zero-count statuses.
  const activeSlices = slices.filter((s) => s.count > 0);
  // Card total = attention assets only (yellow + red). The traffic-light bar is
  // proportional to THIS, so it fills with the Urgent/Pending split rather than
  // leaving a gap for the (hidden) healthy fleet. The header subtitle still
  // shows the full fleet count (assets.total).
  const attentionTotal = activeSlices.reduce((sum, s) => sum + s.count, 0);

  // Nothing yellow or red → the fleet is healthy. The card is attention-only, so
  // show an all-clear state instead of an empty bar (the assets still plot on
  // the map, where green markers remain visible).
  if (attentionTotal === 0) {
    return (
      <DashboardCardShell
        title={title}
        subtitle={String(assets.total)}
        action={action}
        testID="pulse-assets-card"
        emptySlot={
          <EmptyState
            title={t('home.assets.allClear', 'All assets healthy')}
            testID="pulse-assets-card-empty"
          />
        }
      >
        {null}
      </DashboardCardShell>
    );
  }

  return (
    <DashboardCardShell
      title={title}
      subtitle={String(assets.total)}
      action={action}
      testID="pulse-assets-card"
      onHeaderPress={onToggleSelect}
      selected={selected}
      headerPressLabel={t('home.assets.plot', 'Plot assets on the map')}
    >
      <View style={styles.cardBody}>
        {/* Traffic-light proportional bar */}
        <TrafficLightBar
          items={activeSlices}
          total={attentionTotal}
          testID="pulse-assets-card-bar"
        />

        {/* Stat pills: one per status (same pattern as Activity card) */}
        <View style={styles.pillRow} testID="pulse-assets-card-pills">
          {activeSlices.map((s) => (
            <StatPill
              key={s.status}
              color={statusColor(s.status)}
              count={s.count}
              label={labelFor(s.status)}
              active={activeStatus === s.status}
              dimmed={!!activeStatus && activeStatus !== s.status}
              onPress={() => onSelectStatus?.(s.status)}
              testID={`pulse-assets-card-stat-${s.status.toLowerCase().replace(/\s+/g, '-')}`}
            />
          ))}
        </View>

        {/* Status list — dot + label + count rows for the attention statuses
            (Pending / Urgent). The reference shows per-asset name rows; those
            require a richer backend shape (homeAssetTracks / Phase 4). Until then
            we render the status breakdown list from the same data in scope. */}
        <View style={styles.statusList} testID="pulse-assets-card-list">
          {slices.slice(0, 3).map((s) => (
            <StatusRow
              key={s.status}
              slice={s}
              label={labelFor(s.status)}
              active={activeStatus === s.status}
              dimmed={!!activeStatus && activeStatus !== s.status}
              onPress={() => onSelectStatus?.(s.status)}
            />
          ))}
        </View>
      </View>
    </DashboardCardShell>
  );
};

PulseAssetsCard.displayName = 'PulseAssetsCard';

const styles = StyleSheet.create({
  cardBody: {
    flex: 1,
    gap: tokens.spacing.sm,
  },

  // Traffic-light bar ─────────────────────────────────────────────────────────
  bar: {
    height: 10,                      // design: h-2.5
    flexDirection: 'row',
    borderRadius: 4,                 // design: rounded-sm
    overflow: 'hidden',
    gap: 2,                          // design: gap-[2px]
  },
  barSegment: {
    height: '100%' as const,
  },
  barSegmentFirst: {
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  barSegmentLast: {
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  },

  // Loading skeleton ──────────────────────────────────────────────────────────
  loadingBody: {
    gap: tokens.spacing.sm,
  },
  loadingBar: {
    width: '100%',
  },
  loadingList: {
    gap: tokens.spacing.xs,
    flex: 1,
  },

  // Stat pills ────────────────────────────────────────────────────────────────
  pillRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacing.md,          // gap-x-3 (12)
  },
  statPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,                          // design: gap-1.5 (6px)
    paddingVertical: 2,
    paddingHorizontal: tokens.spacing.xs,
    marginHorizontal: -tokens.spacing.xs,
    borderRadius: tokens.borderRadius.full,
  },
  statPillActive: {
    backgroundColor: Colors.role.surfaceInverseSoft,
  },
  statPillDimmed: {
    opacity: 0.45,
  },
  statDot: {
    width: 8,
    height: 8,
    borderRadius: tokens.borderRadius.full,
  },
  statCount: {
    ...tokens.typography.label.medium,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '600',
    color: Colors.role.textStrong,
    fontVariant: ['tabular-nums'],
  },
  statLabel: {
    ...tokens.typography.label.medium,
    fontWeight: '400',
    color: Colors.role.textSoft,
  },

  // Status list ───────────────────────────────────────────────────────────────
  statusList: {
    flex: 1,
    minHeight: 0,
  },
  // -8 margin / +8 padding so the row hover/selection highlight bleeds 8px into
  // the card gutter on both sides (matches the reference -mx-2 px-2). No divider.
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingVertical: tokens.spacing.sm, // py-2 (8px)
    paddingHorizontal: tokens.spacing.sm, // px-2 (8px)
    marginHorizontal: -tokens.spacing.sm, // -mx-2
    borderRadius: tokens.borderRadius.sm,
  },
  statusRowActive: {
    backgroundColor: Colors.role.surfaceInverseSoft,
  },
  statusRowDimmed: {
    opacity: 0.45,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: tokens.borderRadius.full,
    flexShrink: 0,
  },
  statusLabel: {
    ...tokens.typography.label.medium,
    fontSize: 13,
    lineHeight: 18,
    color: Colors.role.textStrong,
    flex: 1,
  },
  statusCount: {
    ...tokens.typography.label.medium,
    fontWeight: '600',
    color: Colors.role.textSoft,
    fontVariant: ['tabular-nums'],
  },
});
